import { Link, useStaticQuery, graphql } from "gatsby";
import React from "react";
import PropTypes from "prop-types";
import styled, { useTheme } from "styled-components";
import Logo from "../images/logo.png";
import Image from "./image.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { checkDarkMode } from "../utils/helpers.js";

const FooterWrapper = styled.footer`
  width: 100%;
  margin-top: 60px;
  content-visibility: auto;
  a {
    color: unset;
  }
`;

const Wrapper = styled.div`
  margin-top: -10px;
  padding: 4rem 2rem;
  background-color: var(--color-white);
  color: var(--color-black);

  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    padding: 2rem 1.5rem;
  }
`;

const InnerWrapper = styled.div`
  margin: 0 auto;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  max-width: 1600px;

  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    flex-flow: column;
  }
`;

const StaticLogo = styled.img`
  height: 50px;
  object-fit: contain;
  filter: ${({ theme }) =>
    theme.isDarkMode ? "grayscale(1) brightness(0) invert(1)" : "none"};
`;

const FooterNav = styled.div`
  display: flex;
  flex-direction: column;
`;

const NavLink = styled(Link)`
  display: block;
  margin: 0 0 0.7rem 0;
  font-weight: 500;
  color: var(--color-black);
  text-decoration: none;
  font-size: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    font-size: 18px;
  }
`;

const InfoText = styled.div`
  p {
    border-right: 1px solid ${({ theme }) => checkDarkMode("#606060", theme)};
    padding-right: 1rem;
    font-size: 12px !important;
    color: ${({ theme }) => checkDarkMode("#606060", theme)};
    margin: 0;
    @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
      font-size: 14px !important;
      border-right: none;
    }
  }
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4rem;

  img {
    filter: ${({ theme }) =>
      theme.isDarkMode ? "grayscale(1) brightness(0) invert(1)" : "none"};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    gap: 1rem;
  }
`;

const BottomContent = styled.div`
  border-top: 1px solid ${({ theme }) => checkDarkMode("#a8a8a8", theme)};
  display: flex;
  justify-content: space-between;
  padding-top: 2rem;
  max-width: 1600px;
  margin: 0 auto;
  margin-top: 2rem;
  gap: 1rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    flex-direction: column;
    border-top: none;
    margin-top: 1rem;
  }
`;
const BottomLink = styled(Link)`
  font-size: 12px;
  text-decoration: none;
  color: var(--color-black);
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    font-size: 14px;
  }
`;

const AccordionHeader = styled.button`
  appearance: none;
  background: transparent;
  border: 0;
  padding: 0;
  margin: 0 0 0.7rem 0;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-weight: 500;
  color: ${({ theme }) => checkDarkMode("#606060", theme)};
  font-size: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    font-size: 18px;
    color: var(--color-black);
    font-family: var(--font-paragraph);
    margin: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    &[aria-expanded="false"]::after {
      content: "+";
      font-size: 20px;
      margin-left: 12px;
    }
    &[aria-expanded="true"]::after {
      content: "−";
      font-size: 20px;
      margin-left: 12px;
    }
  }
`;

const Panel = styled.div`
  overflow: hidden;
  max-height: 0;
  opacity: 0;
  transition:
    max-height 320ms ease,
    opacity 260ms ease;
  will-change: max-height, opacity;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const LinksCol = styled.div`
  display: flex;
  flex-direction: column;

  a,
  a.info-link,
  ${NavLink} {
    display: block;
  }
`;
const Section = styled.div`
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    border-bottom: 1px solid var(--color-black);
    padding: 1rem 0;
    margin-top: 4px;
  }
`;
const LeftBottomDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 2rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    flex-direction: column;
    align-items: start;
    gap: 0.5rem;
  }
`;

function useIsMobile(query) {
  const [m, setM] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(query);
    const on = (e) => setM(e.matches);
    mq.addEventListener
      ? mq.addEventListener("change", on)
      : mq.addListener(on);
    setM(mq.matches);
    return () => {
      mq.removeEventListener
        ? mq.removeEventListener("change", on)
        : mq.removeListener(on);
    };
  }, [query]);
  return m;
}

function Accordion({ label, children, defaultOpen = false }) {
  const [open, setOpen] = React.useState(defaultOpen);
  const ref = React.useRef(null);

  React.useEffect(() => {
    setOpen(defaultOpen);
  }, [defaultOpen]);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open) {
      el.style.maxHeight = el.scrollHeight + "px";
      el.style.opacity = "1";
    } else {
      el.style.maxHeight = "0px";
      el.style.opacity = "0";
    }
  }, [open]);

  return (
    <div>
      <AccordionHeader
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open ? "true" : "false"}
      >
        {label}
      </AccordionHeader>
      <Panel ref={ref}>{children}</Panel>
    </div>
  );
}
Accordion.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  defaultOpen: PropTypes.bool,
};

export default function Footer({ staticQueryData }) {
  const theme = useTheme();
  const isMobile = useIsMobile(`(max-width: ${theme.breakpoints.wideTablet})`);

  const { footerNav, wp, footerLinks } =
    staticQueryData ||
    useStaticQuery(graphql`
      fragment FooterNav on WpMenuItem {
        order
        id
        url
        cssClasses
        label
        target
      }

      query FooterQuery {
        footerNav: wpMenu(locations: { eq: FOOTER_NAV }) {
          menuItems {
            nodes {
              ...FooterNav
              childItems {
                nodes {
                  ...FooterNav
                }
              }
              parentId
            }
          }
        }
        footerLinks: wpMenu(locations: { eq: FOOTER_LINKS }) {
          menuItems {
            nodes {
              ...FooterNav
              parentId
            }
          }
        }

        wp {
          generalSettings {
            title
          }
          crbThemeOptions {
            crbAddress
            crbEmail
            crbPhone
            crbFooterText
            crbLogoFooter {
              media {
                altText
                sourceUrl
                focalPoint {
                  objectPosition
                }
                localFile {
                  childImageSharp {
                    gatsbyImageData(
                      height: 60
                      layout: FIXED
                      placeholder: NONE
                      transformOptions: { fit: CONTAIN }
                    )
                  }
                }
              }
            }
          }
        }
      }
    `);

  return (
    <FooterWrapper
      itemScope
      itemType="http://schema.org/Organization"
      aria-label="footer"
    >
      <meta itemProp="name" content={wp?.generalSettings?.title}></meta>

      <Wrapper>
        <InnerWrapper>
          <Left>
            {wp?.crbThemeOptions?.crbLogoFooter?.media ? (
              <Image
                itemProp="image"
                image={wp.crbThemeOptions.crbLogoFooter.media}
                alt="Logotype"
              />
            ) : (
              <StaticLogo itemProp="image" alt="Logotype" src={Logo} />
            )}
          </Left>

          {footerNav?.menuItems?.nodes
            ?.filter((node) => node.parentId === null)
            ?.sort((a, b) => (a.order || 0) - (b.order || 0))
            ?.map((node) => {
              const hideHeading = node?.cssClasses?.includes("no-heading");

              const children =
                node.childItems?.nodes
                  ?.sort((a, b) => (a.order || 0) - (b.order || 0))
                  ?.map((childNode) => {
                    const isExternal = !!childNode.target;
                    return isExternal ? (
                      <a
                        key={childNode.id}
                        href={childNode.url}
                        target={childNode.target}
                        rel="noreferrer"
                        className="info-link"
                        style={{
                          display: "block",
                          margin: "0 0 0.7rem 0",
                          fontWeight: 500,
                          fontSize: 16,
                          color: "var(--color-black)",
                          textDecoration: "none",
                        }}
                      >
                        {childNode.label}{" "}
                        <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
                      </a>
                    ) : (
                      <NavLink key={childNode.id} to={childNode.url}>
                        {childNode.label}
                      </NavLink>
                    );
                  }) ?? null;

              if (hideHeading) {
                return <FooterNav key={node.id}>{children}</FooterNav>;
              }

              return (
                <FooterNav key={node.id}>
                  <Section>
                    <Accordion label={node.label} defaultOpen={!isMobile}>
                      <LinksCol>{children}</LinksCol>
                    </Accordion>
                  </Section>
                </FooterNav>
              );
            })}
        </InnerWrapper>

        <BottomContent>
          <LeftBottomDiv>
            <InfoText
              dangerouslySetInnerHTML={{
                __html: wp?.crbThemeOptions?.crbFooterText,
              }}
            />
            {footerLinks?.menuItems?.nodes
              ?.filter((node) => !node.cssClasses?.includes("recaptcha"))
              ?.map((node) => (
                <BottomLink key={node.id} to={node.url}>
                  {node.label}
                </BottomLink>
              ))}
          </LeftBottomDiv>
          <div>
            {footerLinks?.menuItems?.nodes
              ?.filter((node) => node.cssClasses?.includes("recaptcha"))
              ?.map((node) => (
                <BottomLink key={node.id} to={node.url}>
                  {node.label}
                </BottomLink>
              ))}
          </div>
        </BottomContent>
      </Wrapper>
    </FooterWrapper>
  );
}

Footer.propTypes = {
  staticQueryData: PropTypes.object,
};
